import axios from 'axios';
import env from '../config/env';
import { isSessionExpiredResponse } from '../utils/sessionExpiry';

// ─── Customer API client ──────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('skinnthrive_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Tenant-ID'] = process.env.REACT_APP_TENANT_SLUG;
  return config;
});

// ─── Token refresh logic ─────────────────────────────────────────────────────
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

// Requests that queued behind a refresh must be released when the refresh fails too — otherwise they wait forever
// and the page shows a spinner that never ends.
const onRefreshFailed = (error) => {
  refreshSubscribers.forEach((cb) => cb(null, error));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    // Never intercept auth endpoints — they legitimately return 401 on bad credentials
    const isAuthEndpoint = url.includes('/auth/') || url.includes('/checkout/verify-otp');

    // The backend answers a dead token with an empty 403 as well as 401 — see utils/sessionExpiry
    if (isSessionExpiredResponse(error) && !isAuthEndpoint && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('skinnthrive_refresh_token');

      if (!refreshToken) {
        // No refresh token — clear session and redirect
        localStorage.removeItem('skinnthrive_token');
        localStorage.removeItem('skinnthrive_refresh_token');
        localStorage.removeItem('skinnthrive_user');
        window.dispatchEvent(new Event('customer-session-expired'));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken, failure) => {
            if (failure) { reject(failure); return; }
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${env.apiBaseUrl}/auth/refresh`,
          { refreshToken },
          { headers: { 'X-Tenant-ID': process.env.REACT_APP_TENANT_SLUG } }
        );
        const newAccessToken = data.accessToken;
        localStorage.setItem('skinnthrive_token', newAccessToken);
        if (data.refreshToken) {
          localStorage.setItem('skinnthrive_refresh_token', data.refreshToken);
        }

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        onRefreshed(newAccessToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear session and redirect
        onRefreshFailed(refreshError);
        localStorage.removeItem('skinnthrive_token');
        localStorage.removeItem('skinnthrive_refresh_token');
        localStorage.removeItem('skinnthrive_user');
        window.dispatchEvent(new Event('customer-session-expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ─── Admin API client ─────────────────────────────────────────────────────────
export const adminApiClient = axios.create({
  baseURL: env.apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

adminApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Tenant-ID'] = process.env.REACT_APP_TENANT_SLUG;
  return config;
});

adminApiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    // Only treat 401 (Unauthorized) as session expiry — token is missing or invalid.
    // 403 (Forbidden) means the user IS authenticated but lacks permission for
    // a specific resource. Do NOT log them out on 403 — let the component handle it.
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.dispatchEvent(new Event('admin-session-expired'));
    }
    return Promise.reject(error);
  }
);

export default apiClient;
